import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import QboCardSingleList from '@ui/Components/QboCardSingleList';
import * as yup from 'yup';
import useForm from '@hooks/useForm';
import { Grid, IconButton } from '@mui/material';
import { QboButton, QboLabelGroup, QboTextField, QboTypography } from '@ui/Components';
import { Delete } from '@mui/icons-material';

const formSchema = yup.object().shape({
  supportTicket: yup.string().required('Support Ticket is required').default(''),
  escalationTicket: yup.string().default(''),
  description: yup.string().default(''),
});

const initialItems = {
  data: [],
  isValid: false,
};

export default function RunFullBackupPayloadSection({
  isEditting,
  isLoading,
  submitFlag,
  original,
  onPayloadValidated,
}) {
  const [items, setItems] = useState(initialItems);
  const resetItems = useCallback(() => {
    if (!original) return;
    setItems({
      data: original.items.map((item) => ({
        'org-owner-id': {
          value: item['org-owner-id'],
          error: null,
        },
        'email-account-id': {
          value: item['email-account-id'],
          error: null,
        },
      })),
      isValid: false,
    });
  }, [original]);

  const validateItems = useCallback(() => {
    const data = items.data.map((item) => ({
      'org-owner-id': {
        value: item['org-owner-id']?.value,
        error: item['org-owner-id']?.value ? null : 'Field is required',
      },
      'email-account-id': {
        value: item['email-account-id']?.value,
        error: item['email-account-id']?.value ? null : 'Field is required',
      },
    }));
    const isValid = data.every(
      (item) => item['org-owner-id']?.value && item['email-account-id']?.value
    );
    setItems({
      data,
      isValid,
    });
  }, [items]);

  const form = useForm(formSchema);

  useEffect(() => {
    if (!original) return;
    resetItems();
    form.set({
      supportTicket: original['support-ticket'] || '',
      escalationTicket: original['escalation-ticket'] || '',
      description: original.description || '',
    });
  }, [original, isEditting]);

  useEffect(() => {
    if (form.isValid && items.isValid) {
      onPayloadValidated({
        items: items.data.map((item) => ({
          'org-owner-id': item['org-owner-id']?.value,
          'email-account-id': item['email-account-id']?.value,
        })),
        'support-ticket': form.values.supportTicket,
        'escalation-ticket': form.values.escalationTicket,
        description: form.values.description,
      });
    } else if (!form.isValid && items.isValid) {
      setItems({ ...items, isValid: false });
    }
  }, [form.isValid, items.isValid]);

  useEffect(() => {
    if (!submitFlag) return;
    validateItems();
    form.reset(true);
    form.validate();
  }, [submitFlag]);

  const handleAddItem = useCallback(() => {
    setItems({
      ...items,
      data: [
        ...items.data,
        {
          'org-owner-id': { value: null, error: null },
          'email-account-id': { value: null, error: null },
        },
      ],
    });
  }, [items]);

  const handleRemoveItem = useCallback(
    (index) => {
      if (!Array.isArray(items.data) || index < 0 || index >= items.data.length) return;
      const data = [...items.data];
      data.splice(index, 1);
      setItems({
        ...items,
        data,
      });
    },
    [items]
  );

  const handleChangeItem = useCallback(
    (index, field, value) => {
      if (!Array.isArray(items.data) || index < 0 || index >= items.length) return;
      const data = [...items.data];
      data[index] = {
        ...data[index],
        [field]: {
          ...data[index][field],
          value: value ? Number.parseInt(value, 10) : null,
        },
      };
      setItems({
        ...items,
        data,
      });
    },
    [items]
  );

  if (!original) return null;

  return (
    <>
      <QboCardSingleList
        key="support-ticket"
        leftContent="Support Ticket"
        rightContent={original['support-ticket'] || '-'}
        editable={isEditting}
        value={form.values.supportTicket}
        valueType="string"
        onChange={(v) => form.change('supportTicket', v)}
        errorMessage={form.errors.supportTicket}
      />
      <QboCardSingleList
        key="escalation-ticket"
        leftContent="Escalation Ticket"
        rightContent={original['escalation-ticket'] || '-'}
        editable={isEditting}
        value={form.values.escalationTicket}
        valueType="string"
        onChange={(v) => form.change('escalationTicket', v)}
        errorMessage={form.errors.escalationTicket}
      />
      <QboCardSingleList
        key="description"
        leftContent="Description"
        rightContent={original.description || '-'}
        editable={isEditting}
        value={form.values.description}
        valueType="string"
        onChange={(v) => form.change('description', v)}
        errorMessage={form.errors.description}
      />

      <div style={{ marginTop: '2rem' }}>
        <h2 style={{ fontWeight: 400 }}>Items</h2>

        {items?.data?.length && (
          <Grid container spacing={2} style={{ marginTop: 0 }}>
            <Grid item xs={5}>
              <QboTypography style={{ fontSize: '1.4rem', fontWeight: 700 }}>
                Organization Owner ID
              </QboTypography>
            </Grid>
            <Grid item xs={5}>
              <QboTypography style={{ fontSize: '1.4rem', fontWeight: 700 }}>
                Email Account ID
              </QboTypography>
            </Grid>
          </Grid>
        )}

        {(items.data ?? []).map((item, index) => (
          /* eslint-disable react/no-array-index-key */
          <Grid container spacing={2} key={index} style={{ marginTop: 0 }}>
            <Grid item xs={5}>
              {isEditting ? (
                <QboTextField
                  id={`org-owner-id-${index}`}
                  placeholder="Organization Owner Id"
                  type="number"
                  fullWidth
                  disabled={!isEditting || isLoading}
                  inputProps={{ maxLength: 225 }}
                  value={item['org-owner-id']?.value}
                  onChange={(e) => handleChangeItem(index, 'org-owner-id', e.currentTarget.value)}
                  errorMessage={item['org-owner-id']?.error}
                />
              ) : (
                <QboLabelGroup description={item['org-owner-id']?.value} />
              )}
            </Grid>

            <Grid item xs={5}>
              {isEditting ? (
                <QboTextField
                  id={`email-account-id-${index}`}
                  placeholder="Email Account Id"
                  type="number"
                  fullWidth
                  disabled={!isEditting || isLoading}
                  inputProps={{ maxLength: 225 }}
                  value={item['email-account-id']?.value}
                  onChange={(e) =>
                    handleChangeItem(index, 'email-account-id', e.currentTarget.value)
                  }
                  errorMessage={item['email-account-id']?.error}
                />
              ) : (
                <QboLabelGroup description={item['email-account-id']?.value} />
              )}
            </Grid>

            {isEditting && (
              <Grid item xs={2}>
                <IconButton
                  style={{ width: 'max-content' }}
                  disabled={isLoading}
                  sx={{
                    position: 'relative',
                    right: '0.7rem',
                  }}
                  onClick={() => handleRemoveItem(index)}>
                  <Delete sx={{ width: '2rem', height: '2rem' }} />
                </IconButton>
              </Grid>
            )}
          </Grid>
        ))}

        {isEditting && (
          <QboButton
            style={{ width: 'max-content', marginTop: '2rem' }}
            disabled={isLoading}
            variant="contained"
            onClick={handleAddItem}>
            Add Item
          </QboButton>
        )}
      </div>
    </>
  );
}

RunFullBackupPayloadSection.propTypes = {
  isEditting: PropTypes.bool,
  isLoading: PropTypes.bool,
  submitFlag: PropTypes.bool,
  original: PropTypes.object,
  onPayloadValidated: PropTypes.func,
};

RunFullBackupPayloadSection.defaultProps = {
  isEditting: false,
  isLoading: false,
  submitFlag: false,
  original: null,
  onPayloadValidated: () => {},
};
